import { useEffect, useState, useMemo } from "react";
import Marquee from "react-fast-marquee";
import { TrendingUp, TrendingDown } from "lucide-react";
import { getTopMarkets } from "@/lib/api";
import { ARC_COIN_LOGO_URL } from "@/assets/arcBrand";

/**
 * Build the displayed coin list with a synthetic ARC entry pegged to
 * USDT for display purposes.
 *
 * Pegging contract (temporary; replace once a real ARC market feed
 * lands):
 *   - ARC price is FORCED to $1.00 regardless of the upstream feed.
 *   - ARC `change_24h` MIRRORS USDT when USDT is present in the feed.
 *   - When USDT is missing, ARC falls back to 0.00%.
 *
 * Pure function — exported separately so unit tests can pin the
 * peg behaviour without mounting the React tree.
 */
export function withSyntheticArc(coins) {
  if (!Array.isArray(coins)) coins = [];
  const usdt = coins.find(
    (c) => typeof c?.symbol === "string" && c.symbol.toUpperCase() === "USDT",
  );
  const arc = {
    id: "synthetic-arc",
    symbol: "ARC",
    name: "ArenaCoin",
    image: ARC_COIN_LOGO_URL,    // matches the ARC medallion used on the marketing page
    price: 1,                    // forced peg
    change_24h: usdt && Number.isFinite(usdt.change_24h) ? usdt.change_24h : 0,
    synthetic: true,             // flag so tests can assert the source
  };
  // Insert ARC at the front so it never gets pushed off-screen by
  // long upstream lists.
  return [arc, ...coins];
}

export default function CryptoTicker() {
  const [coins, setCoins] = useState([]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const data = await getTopMarkets();
        if (mounted) setCoins(data || []);
      } catch (_) {}
    };
    load();
    const id = setInterval(load, 60000);
    return () => { mounted = false; clearInterval(id); };
  }, []);

  const display = useMemo(() => withSyntheticArc(coins), [coins]);

  if (!display.length) {
    return (
      <div className="bg-[#0a0b10] border-y border-white/10 py-3 text-center font-mono text-xs text-white/40" data-testid="ticker-loading">
        // syncing global market...
      </div>
    );
  }

  return (
    <section className="relative bg-[#0a0b10] border-y border-white/10 py-3 marquee-mask" data-testid="crypto-ticker">
      <Marquee speed={45} gradient={false} pauseOnHover>
        {display.concat(display).map((c, i) => {
          const up = (c.change_24h ?? 0) >= 0;
          const isArc = c.symbol === "ARC";
          return (
            <div
              key={`${c.id}-${i}`}
              data-testid={isArc ? "ticker-coin-arc" : `ticker-coin-${c.symbol}`}
              data-synthetic={c.synthetic ? "true" : "false"}
              className="flex items-center gap-3 px-7 border-r border-white/5"
            >
              {c.image ? (
                <img src={c.image} alt={c.symbol} className="w-5 h-5 rounded-full" />
              ) : isArc ? (
                <div className="w-5 h-5 rounded-full bg-[#39FF14]/15 border border-[#39FF14]/50 grid place-items-center font-display text-[8px] font-black text-[#39FF14] tracking-tight">
                  A
                </div>
              ) : (
                <div className="w-5 h-5 rounded-full bg-white/10" />
              )}
              <span className="font-display text-xs font-bold tracking-wider">{c.symbol}</span>
              <span className="font-mono text-xs text-white/80">
                ${(c.price ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </span>
              <span className={`font-mono text-xs flex items-center gap-1 ${up ? "text-[#39FF14]" : "text-[#FF3B30]"}`}>
                {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                {Math.abs(c.change_24h ?? 0).toFixed(2)}%
              </span>
            </div>
          );
        })}
      </Marquee>
    </section>
  );
}
